import axios from 'axios'
import qs from 'qs'
import { defineStore, getActivePinia } from 'pinia'
import { jwtDecode } from 'jwt-decode'
import { globals } from '@/main.ts'
import { isTokenValid, refreshAuthToken } from '@/services/api/apiAuthHelpers'

export const baseURL = import.meta.env.VITE_APIGEE_BASE_URL // URL from .env file
const userActivityEvent = new Event('user-activity')

// Initialize axios with baseURL
const $axios = axios.create({
	baseURL
})

// Basic API wrapper returns either the data from endpoint or the error
export const API = async (props, apiType = null) => {
	if (apiType == 'fhir' && import.meta.env.VITE_USE_POSTMAN == 'true') {
		return APIwrapper(props, import.meta.env.VITE_PST_FHIR_URL + '/')
	} else if (import.meta.env.VITE_USE_POSTMAN == 'true') {
		return APIwrapper(props, import.meta.env.VITE_PST_MBP_URL + '/api/v1')
	} else {
		return APIwrapper(props, baseURL + '/member/myblueprint/v1')
	}
}

export const MessagingAPI = async (props) => {
	if (import.meta.env.VITE_USE_POSTMAN == 'true') {
		return APIwrapper(props, import.meta.env.VITE_PST_MC_URL + '/api/v1/message-center')
	} else {
		return APIwrapper(props, baseURL + '/member/myblueprint/v1/message-center')
	}
}

export const AbcbsAPI = async (props) => {
	if (import.meta.env.VITE_USE_POSTMAN == 'true') {
		return APIwrapper(props, import.meta.env.VITE_PST_MBP_URL + '/api/v1')
	} else {
		return APIwrapper(props, baseURL + '/abcbs/v1')
	}
}

export const CaremarkAPI = async (props) => {
	window.dispatchEvent(userActivityEvent)
	if (sessionStorage.getItem('access_token') && !isTokenValid()) {
		await refreshAuthToken()
	}

	const bearerToken = sessionStorage.getItem('access_token')

	props.headers = {
		...props.headers,
		'Content-Type': 'application/json',
		Authorization: `Bearer ${bearerToken}`
	}

	return APIwrapper(props, baseURL + '/caremark')
}

const APIwrapper = async (props, apiURL) => {
	if (sessionStorage.getItem('access_token') && !isTokenValid()) {
		await refreshAuthToken()
	}
	const { url, method, headers, body, customError } = props
	const bearerToken = sessionStorage.getItem('access_token')
	const defaultHeaders = {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}

	if (bearerToken) defaultHeaders['Authorization'] = `Bearer ${bearerToken}`

	// Added Logrocket session to headers
	if (import.meta.env.VITE_LOGROCKET_PATH && typeof LogRocket != 'undefined') {
		let recordingURL = null
		try {
			LogRocket.getSessionURL(function (sessionURL) {
				if (sessionURL) recordingURL = sessionURL
			})
		} catch (err) {
			console.error(err)
		}
	}

	// if using mock api pass header
	if (import.meta.env.VITE_USE_POSTMAN == 'true') {
		defaultHeaders['X-API-Key'] = import.meta.env.VITE_PST_KEY
	}

	const defaultPayload = {}
	const finalHeader = typeof headers !== 'undefined' ? headers : defaultHeaders

	let finalUrl = url.includes('https') ? url : `${apiURL}/${url}`
	if (import.meta.env.VITE_USE_POSTMAN == 'true') {
		finalUrl = `${apiURL}/${url.replace('https://apipreprod.arkbluecross.com/fhir/', '')}`
	} else {
		finalUrl = url.includes('https') ? url : `${apiURL}/${url}`
	}

	try {
		const { data } = await $axios({
			method,
			url: finalUrl,
			headers: finalHeader,
			data: body || defaultPayload
		})

		return data
	} catch (error) {
		let eCode = 'BP-NFA-100'
		if (props.eCode) eCode = props.eCode

		// display generic error modal if no specific msg is received
		if (!error.response?.data?.validationErrors && typeof customError != 'string') {
			if (typeof customError === 'object' && customError.type === 422) {
				// suppress error modal
			} else if (eCode) {
				if (eCode != 'BP-NFA-010' && eCode != 'BP-NFA-009') globals.$Error(true, eCode)
			} else {
				globals.$Error(true)
			}
		}

		if (eCode) {
			if (import.meta.env.VITE_LOGROCKET_ID) {
				try {
					window.LogRocket.track('apiError', {
						errorCode: eCode,
						apiURL: url,
						errorStatus: error.status,
						errorFull: error
					})
				} catch (err) {}
			}
		}

		const errorRes = error.response ?? null
		throw new Error(error, {
			cause: { eCode: eCode, response: errorRes, customError: customError }
		})
	} finally {
		window.dispatchEvent(userActivityEvent)
	}
}
